// =============================================================================
// TrackerTable.jsx — versão original restaurada
// =============================================================================

import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, Paper, Chip, IconButton, Typography,
  Box, Tooltip, LinearProgress,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const fmtDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, yyyy, a, b] = dash;
    return Number(a) > 12 ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

const COLUMNS = [
  { id: 'title',       label: 'Activity', sortKey: (e) => e.title?.toLowerCase() ?? '' },
  { id: 'teamName',    label: 'Team',     sortKey: (e) => e.teamName?.toLowerCase() ?? '' },
  { id: 'technology',  label: 'Tech',     sortKey: (e) => e.technology?.toLowerCase() ?? '' },
  { id: 'vendor',      label: 'Vendor',   sortKey: (e) => e.vendor?.toLowerCase() ?? '' },
  { id: 'feature',     label: 'Type',     sortKey: (e) => e.feature?.toLowerCase() ?? '' },
  { id: 'startDate',   label: 'Start',    sortKey: (e) => e.startDate ?? '' },
  { id: 'endDate',     label: 'End',      sortKey: (e) => e.endDate ?? '' },
  { id: 'ownerNames',  label: 'Owner',    sortKey: (e) => (e.ownerNames ?? []).join(', ').toLowerCase() },
  { id: 'phases',      label: 'Phases',   sortKey: null },
  { id: '_actions',    label: '',         sortKey: null },
];

const headerCellSx = {
  fontWeight: 700, fontSize: '0.68rem',
  textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'text.disabled', whiteSpace: 'nowrap',
};

const TrackerTable = ({ entries, loading, onRowClick, onDelete, showClosed = false }) => {
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortCol,     setSortCol]     = useState('startDate');
  const [sortDir,     setSortDir]     = useState('asc');

  const handleSort = (colId) => {
    if (sortCol === colId) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
    setPage(0);
  };

  const displayed = useMemo(() => {
    const filtered = showClosed
      ? entries
      : entries.filter((e) => (e.status ?? 'Open') !== 'Closed');
    const col = COLUMNS.find((c) => c.id === sortCol);
    if (!col?.sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.sortKey(a);
      const bv = col.sortKey(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [entries, showClosed, sortCol, sortDir]);

  const start     = page * rowsPerPage;
  const paginated = displayed.slice(start, start + rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {loading && <LinearProgress sx={{ height: 2 }} />}
      {!loading && entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities yet. Click <strong>+ New Activity</strong> to start.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={headerCellSx}>
                      {col.sortKey ? (
                        <TableSortLabel
                          active={sortCol === col.id}
                          direction={sortCol === col.id ? sortDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((entry) => {
                  const isClosed = (entry.status ?? 'Open') === 'Closed';
                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      onClick={() => onRowClick(entry)}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { border: 0 },
                        opacity: isClosed ? 0.55 : 1,
                      }}
                    >
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={entry.title} placement="top" enterDelay={400}>
                          <Typography variant="body2" fontWeight={600} noWrap
                            sx={{ color: isClosed ? 'text.disabled' : 'inherit' }}>
                            {entry.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.teamName || ''} placement="top" enterDelay={400} disableHoverListener={!entry.teamName}>
                          <span>{entry.teamName || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.technology || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.vendor || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.feature || ''} placement="top" enterDelay={400} disableHoverListener={!entry.feature}>
                          <span>{entry.feature || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.startDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.endDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const owners = (entry.ownerNames?.length ? entry.ownerNames.join(', ') : entry.ownerName) || '—';
                          return (
                            <Tooltip title={owners} placement="top" enterDelay={400} disableHoverListener={owners === '—'}>
                              <span>{owners}</span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const phases = entry.phases || [];
                          const rollbackCount = phases.filter(ph => ph.label === 'Rollback').length;
                          const phaseCount = phases.length - rollbackCount;
                          return (
                            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                              {phaseCount > 0 && (
                                <Chip label={`${phaseCount} Phase${phaseCount !== 1 ? 's' : ''}`}
                                  size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                              {rollbackCount > 0 && (
                                <Chip label={`${rollbackCount} Rollback${rollbackCount !== 1 ? 's' : ''}`}
                                  size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                              {phases.length === 0 && (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onRowClick(entry)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => onDelete(entry.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={displayed.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </>
      )}
    </Paper>
  );
};

export default TrackerTable;